/* SILO: Navigation behaviour.
   <sd-nav>        — desktop mega menus/dropdowns with hover intent; slots itself
                     into the theme header's grid so it inherits sticky + colors.
   <sd-nav-drawer> — mobile slide-in drawer with a panel stack.
   Idempotent: safe to load once per page alongside theme editor re-renders. */

(() => {
  'use strict';

  const DESKTOP_MQ = window.matchMedia('(min-width: 769px)');
  const OPEN_DELAY = 60;
  const CLOSE_DELAY = 240;

  // Intuitive exit: on desktop, clicking anywhere outside the open search bar
  // closes it (routed through the stock toggle so its own logic runs)
  let outsideSearchBound = false;
  const bindOutsideSearchClose = () => {
    if (outsideSearchBound) return;
    outsideSearchBound = true;
    document.addEventListener('pointerdown', (evt) => {
      if (!DESKTOP_MQ.matches) return;
      const header = document.querySelector('store-header');
      if (!header || !header.dataset.isSearchMinimised) return;
      if (header.classList.contains('search-is-collapsed')) return;
      if (evt.target.closest('.header__search, .js-show-search, .js-show-search-left')) return;
      header.querySelector('.js-show-search, .js-show-search-left')?.click();
    });
  };

  if (!customElements.get('sd-nav')) {
    class SdNav extends HTMLElement {
      connectedCallback() {
        if (this.sdInit) return;
        this.sdInit = true;
        SdNav.instance = this;
        requestAnimationFrame(() => this.init());
      }

      init() {
        this.slotIntoHeader();
        bindOutsideSearchClose();
        this.openItem = null;
        // Slotting changes the header's height — re-measure --header-end
        requestAnimationFrame(() => this.refreshHeaderEnd());

        this.querySelectorAll('[data-sdnav-item]').forEach((li) => {
          li.addEventListener('mouseenter', () => {
            if (!DESKTOP_MQ.matches) return;
            clearTimeout(li.sdCloseT);
            li.sdOpenT = setTimeout(() => this.open(li), OPEN_DELAY);
          });
          li.addEventListener('mouseleave', () => {
            clearTimeout(li.sdOpenT);
            li.sdCloseT = setTimeout(() => this.close(li), CLOSE_DELAY);
          });

          li.querySelector('[data-sdnav-toggle]')?.addEventListener('click', () => {
            li.classList.contains('is-open') ? this.close(li) : this.open(li);
          });

          // Touch: first tap on a parent link opens the panel, second navigates
          const link = li.querySelector('[data-sdnav-link]');
          if (link) {
            li.addEventListener('pointerdown', (evt) => {
              li.sdTouch = evt.pointerType !== 'mouse';
            });
            link.addEventListener('click', (evt) => {
              if (li.sdTouch && DESKTOP_MQ.matches && !li.classList.contains('is-open')) {
                evt.preventDefault();
                this.open(li);
              }
            });
          }
        });

        this.querySelector('[data-sdnav-burger]')?.addEventListener('click', () => {
          const drawer = document.getElementById(this.dataset.drawer);
          if (!drawer) return;
          drawer.classList.contains('is-open') ? drawer.close() : drawer.open();
        });

        this.addEventListener('focusout', (evt) => {
          if (!this.contains(evt.relatedTarget)) this.closeAll();
        });

        document.addEventListener('keydown', (evt) => {
          if (evt.key !== 'Escape' || !this.openItem) return;
          const li = this.openItem;
          this.closeAll();
          li.querySelector('[data-sdnav-toggle]')?.focus();
        });

        document.addEventListener('click', (evt) => {
          if (this.openItem && !this.contains(evt.target)) this.closeAll();
        });

        window.addEventListener('on:breakpoint-change', () => this.closeAll());

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:block:select', (evt) => {
            const li = this.querySelector(`[data-block-id="${evt.detail.blockId}"]`);
            if (li) this.open(li);
          });
          document.addEventListener('shopify:block:deselect', () => this.closeAll());
          // If the theme header section re-renders it destroys the slotted nav;
          // re-adopt the surviving instance into the fresh grid.
          document.addEventListener('shopify:section:load', () => {
            const inst = SdNav.instance;
            if (inst && !document.documentElement.contains(inst)) {
              inst.classList.remove('sd-nav--slotted');
              inst.slotIntoHeader();
            }
          });
        }
      }

      slotIntoHeader() {
        if (this.dataset.integrate !== 'true') return;
        const grid = document.querySelector('store-header .header__grid, .header .header__grid');
        if (!grid) return;
        if (!grid.contains(this)) {
          grid.querySelectorAll(':scope > sd-nav').forEach((el) => {
            if (el !== this) el.remove();
          });
          grid.appendChild(this);
        }
        this.classList.add('sd-nav--slotted');
        // Single-line mode restyles the header's own children (logo/search/icons),
        // so mark the header element itself for CSS to scope off
        const headerEl = grid.closest('.header');
        if (headerEl) headerEl.classList.toggle('header--sd-inline', this.dataset.layout === 'inline');
      }

      refreshHeaderEnd() {
        const header = document.querySelector('store-header');
        if (!header) return;
        if (typeof header.setMenuHeight === 'function') header.setMenuHeight();
        if (typeof header.setHeaderEnd === 'function') header.setHeaderEnd();
      }

      open(li) {
        if (this.openItem && this.openItem !== li) this.close(this.openItem);
        clearTimeout(li.sdCloseT);
        this.refreshHeaderEnd();
        const drop = li.querySelector('.sd-nav__panel--drop');
        if (drop) {
          const rect = li.getBoundingClientRect();
          const half = Math.max(drop.offsetWidth / 2, 130);
          const center = Math.min(
            Math.max(rect.left + rect.width / 2, half + 8),
            window.innerWidth - half - 8
          );
          drop.style.left = `${center.toFixed(1)}px`;
        }
        li.classList.add('is-open');
        li.querySelectorAll('[data-sdnav-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
        this.openItem = li;
        if (li.classList.contains('sd-nav__li--mega')) this.classList.add('sd-nav--open');
      }

      close(li) {
        li.classList.remove('is-open');
        li.querySelectorAll('[data-sdnav-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        if (this.openItem === li) this.openItem = null;
        if (!this.openItem) this.classList.remove('sd-nav--open');
      }

      closeAll() {
        this.querySelectorAll('[data-sdnav-item].is-open').forEach((li) => this.close(li));
      }
    }

    customElements.define('sd-nav', SdNav);
  }

  if (!customElements.get('sd-nav-drawer')) {
    class SdNavDrawer extends HTMLElement {
      connectedCallback() {
        if (this.sdInit) return;
        this.sdInit = true;
        requestAnimationFrame(() => this.init());
      }

      init() {
        this.box = this.querySelector('.sd-drawer__box');
        this.stack = ['root'];

        // The header's accent/color vars are scoped to the .header element —
        // copy the resolved values so the drawer matches the nav styling.
        const header = document.querySelector('.header');
        if (header) {
          const styles = getComputedStyle(header);
          const accent = styles.getPropertyValue('--header-accent-color').trim();
          const text = styles.getPropertyValue('--text-color').trim();
          const bg = styles.getPropertyValue('--nav-bg-color').trim();
          if (accent) this.style.setProperty('--sddr-accent', accent);
          if (text) this.style.setProperty('--sddr-text', text);
          if (bg) this.style.setProperty('--sddr-bg', bg);
        }

        this.addEventListener('click', (evt) => {
          if (evt.target.closest('[data-sddr-close]')) return this.close();
          const opener = evt.target.closest('[data-sddr-open]');
          if (opener) return this.show(opener.dataset.sddrOpen);
          if (evt.target.closest('[data-sddr-back]')) return this.back();
        });

        document.addEventListener('keydown', (evt) => {
          if (!this.classList.contains('is-open')) return;
          if (evt.key === 'Escape') return this.close();
          if (evt.key === 'Tab') this.trapFocus(evt);
        });

        window.addEventListener('on:breakpoint-change', () => {
          if (DESKTOP_MQ.matches && this.classList.contains('is-open')) this.close();
        });
      }

      panel(id) {
        return this.querySelector(`[data-sddr-panel="${id}"]`);
      }

      open() {
        this.returnFocusTo = document.activeElement;
        this.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        this.syncBurger(true);
        this.querySelector('.sd-drawer__close')?.focus();
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        this.syncBurger(false);
        if (this.returnFocusTo && document.contains(this.returnFocusTo)) this.returnFocusTo.focus();
        // Reset to the root panel once the slide-out finishes
        clearTimeout(this.resetT);
        this.resetT = setTimeout(() => {
          this.querySelectorAll('.sd-drawer__panel').forEach((p) => p.classList.remove('is-active', 'is-under'));
          this.panel('root')?.classList.add('is-active');
          this.stack = ['root'];
        }, 400);
      }

      show(id) {
        const next = this.panel(id);
        if (!next) return;
        const current = this.panel(this.stack[this.stack.length - 1]);
        current?.classList.remove('is-active');
        current?.classList.add('is-under');
        next.classList.add('is-active');
        next.scrollTop = 0;
        this.stack.push(id);
        next.querySelector('[data-sddr-back]')?.focus({ preventScroll: true });
      }

      back() {
        if (this.stack.length < 2) return;
        const current = this.panel(this.stack.pop());
        current?.classList.remove('is-active');
        const prev = this.panel(this.stack[this.stack.length - 1]);
        prev?.classList.remove('is-under');
        prev?.classList.add('is-active');
      }

      syncBurger(isOpen) {
        document.querySelectorAll(`[data-sdnav-burger][aria-controls="${this.id}"]`).forEach((b) => {
          b.setAttribute('aria-expanded', String(isOpen));
        });
      }

      trapFocus(evt) {
        const focusables = this.box.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const visible = [...focusables].filter((el) => el.offsetParent !== null);
        if (!visible.length) return;
        const first = visible[0];
        const last = visible[visible.length - 1];
        if (evt.shiftKey && document.activeElement === first) {
          evt.preventDefault();
          last.focus();
        } else if (!evt.shiftKey && document.activeElement === last) {
          evt.preventDefault();
          first.focus();
        }
      }
    }

    customElements.define('sd-nav-drawer', SdNavDrawer);
  }
})();
